// HorScroll V1.3
// Delegates horizontal scroll handling using a single body listener in capture phase.
// Tracks pairs of {scroll: 'scroll-class', holder: 'holder-class'} and updates holder
// dataset attributes [data-left] and [data-right] based on scroll position of scroll element

// optional dataset attributes on holder:
// [data-center]: if present, content will be centered
// [data-mask]: if present, a mask-image fade instead of 2 gradients. 
// Mask advantage: works on any background, disadvantage: doesn't transition, scrollbar is included in mask

// init:
// horScroller = new HorScroll({ classes: [{scroll: 'filter__content', holder: 'filter'}] });
// horScroller.init();
// horScroller.refreshElements() when content changes. Not needed for scroll listener, but for adding dataset-scroll="true"

// required properties:
// none

// methods:
// horScroller.init(): initialize scroll handling and query initial elements
// horScroller.destroy(): remove listeners and clear dataset attributes
// horScroller.refreshElements(): query and update tracked elements using configured classes
// horScroller.checkMaxScroll(el, holderClass): update scroll position attributes for a single element (public helper)

// changelog:
// 1.0 initial version
// 1.1 perfect padding, center option, margin bottom
// 1.2 add mask-image option
// 1.3 add centerOnActiveElement option and logic to refreshElements

export default class HorScroll {
  constructor({ 
    holderClass = 'hor-scroll', 
    contentClass = 'hor-scroll__content',
    centerOnActiveElement = true, // if true, will scroll to center active element on refreshElements
  } = {}) {
    this.holderClass = holderClass;
    this.contentClass = contentClass;
    this.centerOnActiveElement = centerOnActiveElement;

    this.initialized = false;

    this.boundHandleScroll = this.#handleScroll.bind(this);
    this.boundHandleResize = this.#handleResize.bind(this);
  }

  init = () => {
    if (this.initialized) return;
    // console.log('HorScroll init');

    // capture phase so we get scroll events from descendants without adding per-element listeners
    document.body.addEventListener('scroll', this.boundHandleScroll, { capture: true, passive: true });
    window.addEventListener('resize', this.boundHandleResize, { passive: true });

    this.initialized = true;
  }

  destroy = () => {
    if (!this.initialized) return;
    document.body.removeEventListener('scroll', this.boundHandleScroll, { capture: true });
    window.removeEventListener('resize', this.boundHandleResize);

    // clear left/right dataset flags from holders
    const holders = document.querySelectorAll('.' + this.holderClass);
    holders.forEach(holder => {
      try {
        delete holder.dataset.left;
        delete holder.dataset.right;
        delete holder.dataset.scroll;
      } catch (e) {}
    });

    this.initialized = false;
  }

  #handleScroll(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // check if target is a hor-scroll content element (or inside one)
    const sel = '.' + this.contentClass;
    const contentEl = target.matches(sel) ? target : target.closest(sel);
    if (contentEl) {
      this.checkMaxScroll(contentEl, this.holderClass);
    }
  }

  #handleResize() {
    // recalc sizes for all tracked elements
    this.refreshElements();
  }

  // query DOM for configured classes and run a size/position update on them
  refreshElements = (newContent = false) => {
    if (!this.initialized) return;

    // gather all holders and their content elements
    const holderEls = document.querySelectorAll('.' + this.holderClass);
    holderEls.forEach(holderEl => {
      holderEl.dataset.scroll = 'true';

      const contentEl = holderEl.querySelector('.' + this.contentClass);
      if (contentEl) {
        this.checkMaxScroll(contentEl, this.holderClass);
        
        // If requested, center the active element, so it's visible after content switch
        if (this.centerOnActiveElement && newContent) {
          const activeEl = holderEl.querySelector('[data-hor-scroll-active="true"]');
          if (activeEl && contentEl.contains(activeEl)) {
            const scrollWidth = contentEl.scrollWidth;
            const width = contentEl.clientWidth;
            const maxScrollX = Math.max(0, scrollWidth - width);

            const activeLeft = activeEl.offsetLeft;
            const activeCenter = activeLeft + (activeEl.offsetWidth / 2);
            let target = Math.round(activeCenter - (width / 2));
            target = Math.max(0, Math.min(target, maxScrollX));

            contentEl.scrollLeft = target;

            this.checkMaxScroll(contentEl, this.holderClass);
          }
        }
      }
    });
  }

  checkMaxScroll = (el, holder) => {
    const holderEl = el.closest('.' + holder);
    if (!holderEl) return;

    const { scrollWidth } = el;
    const width = el.clientWidth;
    const maxScrollX = parseInt(scrollWidth - width, 10);
    const scrollX = el.scrollLeft;

    holderEl.dataset.left = scrollX < 1 ? 'true' : 'false';
    holderEl.dataset.right = scrollX > maxScrollX - 1 ? 'true' : 'false';
  }
}
